import React from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Typography,
  Box,
} from '@mui/material';
import {
  People as PeopleIcon,
  AttachMoney as MoneyIcon,
  Assignment as AssignmentIcon,
  Casino as CasinoIcon,
  ShoppingCart as ShoppingCartIcon,
  Receipt as ReceiptIcon,
  BeachAccess as VacationIcon,
  Sick as SickIcon,
  AccountBalance as AccountBalanceIcon,
  RestaurantMenu as RestaurantMenuIcon,
  Event as EventIcon,
  BookOnline as ReservationIcon,
  Description as FormsIcon,
  Work as WorkIcon,
  VolunteerActivism as DonationIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

interface DashboardCard {
  title: string;
  description: string;
  icon: React.ReactNode;
  path: string;
  actionLabel: string;
}

const cards: DashboardCard[] = [
  {
    title: 'Employees',
    description: 'Manage restaurant staff, add new employees, and track their information.',
    icon: <PeopleIcon />,
    path: '/employees',
    actionLabel: 'Manage Employees',
  },
  {
    title: 'Tip Out Log',
    description: 'Create and submit daily tip out log entries for AM and PM shifts.',
    icon: <MoneyIcon />,
    path: '/tip-out-log',
    actionLabel: 'New Tip Out Log',
  },
  {
    title: 'Tip Out Log Forms',
    description: 'View and manage all submitted tip out log forms and reports.',
    icon: <AssignmentIcon />,
    path: '/tip-out-log-forms',
    actionLabel: 'View Forms',
  },
  {
    title: 'Lottery',
    description: 'Manage lottery entries, track sales, and monitor daily lottery operations.',
    icon: <CasinoIcon />,
    path: '/lottery',
    actionLabel: 'Manage Lottery',
  },
  {
    title: 'Lottery Purchase',
    description: 'Record lottery book purchases with invoice numbers and book amounts.',
    icon: <ShoppingCartIcon />,
    path: '/lottery-purchase',
    actionLabel: 'View Purchases',
  },
  {
    title: 'Misc. Purchases',
    description: 'Track miscellaneous purchases with purchaser details and cash amounts.',
    icon: <ReceiptIcon />,
    path: '/misc-purchases',
    actionLabel: 'View Purchases',
  },
  {
    title: 'Donation / Charity',
    description: 'Track charitable donations, receipts, and tax-deductible contributions.',
    icon: <DonationIcon />,
    path: '/donations',
    actionLabel: 'View Donations',
  },
  {
    title: 'Vacation',
    description: 'Manage employee vacation requests with dates, duration, reason, and emergency contact.',
    icon: <VacationIcon />,
    path: '/vacation',
    actionLabel: 'View Vacations',
  },
  {
    title: 'Sick Form',
    description: 'Track employee sick days by position, date range, duration, and reason.',
    icon: <SickIcon />,
    path: '/sick-form',
    actionLabel: 'View Sick Forms',
  },
  {
    title: 'New Balance',
    description: 'Enter daily balance data for AM/PM shifts, paid outs, tips, and keno cash.',
    icon: <AccountBalanceIcon />,
    path: '/new-balance',
    actionLabel: 'New Balance',
  },
  {
    title: 'New Balance Forms',
    description: 'View and print previously submitted balance forms by date.',
    icon: <AssignmentIcon />,
    path: '/new-balance-forms',
    actionLabel: 'View Forms',
  },
  {
    title: 'Daily Specials',
    description: 'Update the daily specials menu visible on the restaurant website.',
    icon: <RestaurantMenuIcon />,
    path: '/daily-specials',
    actionLabel: 'Edit Specials',
  },
  {
    title: 'Event Forms List',
    description: 'View all submitted event reservation inquiry forms from guests.',
    icon: <EventIcon />,
    path: '/event-forms-list',
    actionLabel: 'View Events',
  },
  {
    title: 'Event Reservations',
    description: 'Submit and manage event reservation forms for private dining.',
    icon: <FormsIcon />,
    path: '/event-reservations',
    actionLabel: 'View Reservations',
  },
  {
    title: 'Work With Us',
    description: 'Review job applications submitted through the website.',
    icon: <WorkIcon />,
    path: '/work-with-us',
    actionLabel: 'View Applications',
  },
  {
    title: 'Reservation Forms',
    description: 'View all table reservation requests submitted by guests.',
    icon: <ReservationIcon />,
    path: '/reservation-forms-list',
    actionLabel: 'View Reservations',
  },
];

const Dashboard: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Dashboard
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Welcome back! Choose an option below to manage your restaurant.
      </Typography>

      <Grid container spacing={3}>
        {cards.map((card) => (
          <Grid item xs={12} sm={6} md={4} key={card.path}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                border: '1px solid #e0e0e0',
                transition: 'box-shadow 0.2s',
                '&:hover': { boxShadow: 4 },
              }}
            >
              <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 44,
                      height: 44,
                      borderRadius: 2,
                      backgroundColor: '#b71c1c',
                      color: '#fff',
                      mr: 2,
                      flexShrink: 0,
                      '& svg': { fontSize: 24 },
                    }}
                  >
                    {card.icon}
                  </Box>
                  <Typography variant="h6" component="h2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                    {card.title}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {card.description}
                </Typography>
              </CardContent>
              <CardActions sx={{ px: 2, pb: 2 }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => navigate(card.path)}
                  sx={{
                    borderColor: '#b71c1c',
                    color: '#b71c1c',
                    '&:hover': { backgroundColor: '#b71c1c', color: '#fff', borderColor: '#b71c1c' },
                  }}
                >
                  {card.actionLabel}
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
};

export default Dashboard;
